import {
  AdornedInput,
  Alert,
  Anchor,
  Box,
  Button,
  ButtonGroup,
  Card,
  Divider,
  FormField,
  FormHelp,
  Heading,
  Input,
  Label,
  LoadingMask,
  Modal,
  Paragraph,
  Text,
  Textarea,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import { CharacterCountFormHelp, useCharacterCount } from '@zg-rentals/visual-cohesion';
import { validate } from 'email-validator';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useAddressAutocomplete } from '../../hooks/useAddressAutocomplete';
import { AddressAutocomplete, type AddressResponse } from '../AddressAutocomplete';
import { useEffect, useState } from 'react';
import { logError } from '@zg-rentals/log-error';
import { useGetReportsByZip } from '../../hooks/useGetReports';
import {
  GetApplicationsForUserDocument,
  ShareApplicationDocument,
  type ShareRentalApplicationPropertyAddressInput,
  type UserStatus,
  UserStatusType,
} from '../../graphql/queries';
import { SUBMISSION_TYPES, useBaseContext } from '../../contexts/BaseContext';
import { useMutation } from '@apollo/client';
import { CreditReportOverview } from '../Reports/ReportCards/CreditReportOverview';
import { BackgroundReportOverview } from '../Reports/ReportCards/BackgroundReportOverview';
import type { ReportType } from '../../models';
import { ReportModal } from '../Reports';
import { useIsFetching } from '@zg-rentals/react-query5';
import { useAuth } from '../AuthProvider';
import { APPLICATIONS_FAQ_URL } from '../../constants/urls';
import { emitShareApplicationNextEvent, emitShareApplicationSharedEvent } from '../../analytics/clickstreamEvents';
import { bootstrap } from '../../bootstrap';
import { transformReportData } from '../../utils/reports/report-utils';
import { useApplicationContextOrUndefined } from '../../contexts/ApplicationContext';

type ShareFormData = {
  landlordEmail: string;
  addressString: string;
  propertyAddress?: ShareRentalApplicationPropertyAddressInput;
  unit?: string;
  message?: string;
};

const MAX_EMAIL_LEN = 320;
const MAX_UNIT_LEN = 16;
const MAX_MESSAGE_LEN = 10_000;

export function ApplicationShare({
  onClose,
  userStatuses,
  initialAddress,
  initialUnit,
}: {
  onClose: () => void;
  userStatuses: Array<UserStatus>;
  initialAddress?: string;
  initialUnit?: string;
}) {
  const { setSubmittedApplication } = useBaseContext();
  const applicationContext = useApplicationContextOrUndefined();
  const form = useForm<ShareFormData>();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = form;
  const [partialAddress, setPartialAddress, queryData] = useAddressAutocomplete({ initialAddress });
  const [isOnReview, setIsOnReview] = useState(false);
  const { enqueueToast } = useToast();
  const [shareMutation, { loading: isSharing }] = useMutation(ShareApplicationDocument, {
    refetchQueries: [GetApplicationsForUserDocument],
  });
  const { user: userData } = useAuth();

  const addressString = watch('addressString');
  const zip = watch('propertyAddress.zip') as string;
  const { isFocused: isEmailFocused, onFocus: onEmailFocus, onBlur: onEmailBlur } = useCharacterCount();
  const { isFocused: isUnitFocused, onFocus: onUnitFocus, onBlur: onUnitBlur } = useCharacterCount();
  const { isFocused: isMessageFocused, onFocus: onMessageFocus, onBlur: onMessageBlur } = useCharacterCount();
  const emailLength = watch('landlordEmail')?.length ?? 0;
  const unitLength = watch('unit')?.length ?? 0;
  const messageLength = watch('message')?.length ?? 0;

  useEffect(() => {
    // If the url has ?shareAddress we need to wait for the data from the autocomplete lookup to finish
    // and then set the propertyAddress as if the user clicked the result
    const addresses = queryData?.data?.response?.addresses;
    if (initialAddress && addresses?.length && !addressString) {
      const address = addresses.find(({ name }) => name === initialAddress);
      if (address) {
        setValue('addressString', initialAddress);
        setValue('unit', initialUnit);
        setValue('propertyAddress', {
          propertyId: address.propertyId,
          street: address.street,
          city: address.city,
          state: address.state,
          zip: address.zip,
          unit: initialUnit,
        });
      }
    }
  }, [initialAddress, queryData?.data, addressString]);

  function onClickNext(_: ShareFormData) {
    setIsOnReview(true);
    emitShareApplicationNextEvent(applicationContext);
  }

  async function onClickShare(data: ShareFormData) {
    if (data.propertyAddress) {
      try {
        emitShareApplicationSharedEvent(applicationContext);
        await shareMutation({
          variables: {
            landlordEmail: data.landlordEmail,
            propertyAddress: {
              ...data.propertyAddress,
              unit: data.unit,
            },
            message: data.message,
          },
        });
        bootstrap.monitor.count({ name: `ApplicationShare onClickShare` });
        bootstrap.monitor.count({ name: `ApplicationShare onClickShare state:${data.propertyAddress.state}` });
        setSubmittedApplication?.({
          type: SUBMISSION_TYPES.SHARED,
          address: {
            street: data.propertyAddress.street,
            city: data.propertyAddress.city,
            state: data.propertyAddress.state,
            zip: data.propertyAddress.zip,
            unit: data.unit,
          },
        });
        onClose();
      } catch (err) {
        logError({ error: err, context: 'ApplicationShare - shareApplicationMutation error' });
        enqueueToast(<Toast tone="critical">We couldn’t share your application. Try again.</Toast>);
      }
    }
  }

  const formSubmitHandler = form.handleSubmit(onClickNext);
  const isFetching = !!useIsFetching();

  return (
    <FormProvider {...form}>
      <Modal.Root open={true} onOpenChange={(open) => !open && onClose()} size={{ base: 'fullScreen', md: 'lg' }}>
        <Modal.Portal>
          <Modal.Backdrop>
            <Modal.Content asChild>
              <form onSubmit={formSubmitHandler}>
                <Modal.Header>
                  <Heading level={2}>Share application</Heading>
                </Modal.Header>
                <Divider />
                <Modal.Body>
                  {!isOnReview ? (
                    <>
                      <Paragraph textStyle="body-sm" css={{ marginBottom: 'default' }}>
                        You can add a personal note to share with the rental manager along with your application. We
                        suggest checking with the rental manager to make sure they accept Zillow applications.
                      </Paragraph>
                      <FormField
                        required
                        css={{ marginTop: 'default', marginBottom: 'default' }}
                        label={
                          <Label htmlFor="email" id="landlord-email-label">
                            Rental manager’s email
                          </Label>
                        }
                        control={
                          <Input
                            aria-labelledby="landlord-email-label"
                            {...register('landlordEmail', {
                              required: 'Email is required',
                              maxLength: MAX_EMAIL_LEN,
                              validate: (email: string) => {
                                if (!validate(email)) {
                                  return 'Enter a valid email';
                                } else if (email === userData?.email) {
                                  return 'Application can’t be shared with your own email';
                                } else {
                                  return true;
                                }
                              },
                            })}
                            id="email"
                            onFocus={onEmailFocus}
                            onBlur={onEmailBlur}
                          />
                        }
                        description={
                          <CharacterCountFormHelp
                            currentLength={emailLength}
                            maxLength={MAX_EMAIL_LEN}
                            isFocused={isEmailFocused}
                            label="Email"
                            helperText={errors.landlordEmail?.message}
                          />
                        }
                        error={emailLength > MAX_EMAIL_LEN || !!errors.landlordEmail}
                      />
                      <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 'loose' }}>
                        <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 6' } }}>
                          <Controller
                            name="addressString"
                            rules={{
                              required: 'A USPS validated address is required',
                            }}
                            render={({ field: { onChange, value } }) => {
                              const handleAddressSelect = (newAddress: AddressResponse) => {
                                onChange(newAddress.name);
                                setValue('propertyAddress', {
                                  propertyId: newAddress.propertyId,
                                  street: newAddress.street,
                                  city: newAddress.city,
                                  state: newAddress.state,
                                  zip: newAddress.zip,
                                });
                              };

                              return (
                                <FormField
                                  required
                                  label={<Label id="property-address-label">Rental property address</Label>}
                                  control={
                                    <AddressAutocomplete
                                      value={value}
                                      onInputChange={() => onChange('')}
                                      partialAddress={partialAddress}
                                      onSetPartialAddress={setPartialAddress}
                                      queryData={queryData}
                                      onAddressSelect={handleAddressSelect}
                                      error={!!errors.addressString}
                                      aria-labelledby="property-address-label"
                                    />
                                  }
                                  description={
                                    errors.addressString && <FormHelp>{errors.addressString.message}</FormHelp>
                                  }
                                  error={!!errors.addressString}
                                />
                              );
                            }}
                          />
                        </Box>
                        <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 6' } }}>
                          <FormField
                            css={{ marginBottom: 'default' }}
                            label={<Label htmlFor="unitNumber">Unit number</Label>}
                            control={
                              <AdornedInput.Root>
                                <AdornedInput.Adornment>#</AdornedInput.Adornment>
                                <AdornedInput.Input
                                  {...register('unit', {
                                    maxLength: MAX_UNIT_LEN,
                                  })}
                                  id="unitNumber"
                                  onFocus={onUnitFocus}
                                  onBlur={onUnitBlur}
                                />
                              </AdornedInput.Root>
                            }
                            description={
                              <CharacterCountFormHelp
                                currentLength={unitLength}
                                maxLength={MAX_UNIT_LEN}
                                isFocused={isUnitFocused}
                                label="Unit"
                              />
                            }
                            error={unitLength > MAX_UNIT_LEN || !!errors.unit}
                            optional
                          />
                        </Box>
                      </Box>
                      <FormField
                        label={<Label>Add a message</Label>}
                        control={
                          <Textarea
                            css={{ resize: 'vertical' }}
                            {...register('message', {
                              maxLength: MAX_MESSAGE_LEN,
                            })}
                            onFocus={onMessageFocus}
                            onBlur={onMessageBlur}
                          />
                        }
                        description={
                          <CharacterCountFormHelp
                            currentLength={messageLength}
                            maxLength={MAX_MESSAGE_LEN}
                            isFocused={isMessageFocused}
                            label="Message"
                          />
                        }
                        error={messageLength > MAX_MESSAGE_LEN || !!errors.message}
                        optional
                      />
                    </>
                  ) : (
                    <ShareReview zip={zip} userStatuses={userStatuses} />
                  )}
                </Modal.Body>
                <Modal.Footer>
                  {!isOnReview ? (
                    <ButtonGroup aria-label="Share application actions">
                      <TextButton onClick={onClose}>Cancel</TextButton>
                      <Button tone="brand" emphasis="filled" onClick={handleSubmit(onClickNext)}>
                        Next
                      </Button>
                    </ButtonGroup>
                  ) : (
                    <ButtonGroup aria-label="Share application actions">
                      <TextButton onClick={() => setIsOnReview(false)}>Back</TextButton>
                      <Button
                        tone="brand"
                        emphasis="filled"
                        loading={isSharing}
                        disabled={isFetching}
                        onClick={handleSubmit(onClickShare)}
                      >
                        Share application
                      </Button>
                    </ButtonGroup>
                  )}
                </Modal.Footer>
                <Modal.CloseButton />
              </form>
            </Modal.Content>
          </Modal.Backdrop>
        </Modal.Portal>
      </Modal.Root>
    </FormProvider>
  );
}

function ShareReview({ zip, userStatuses }: { zip: string; userStatuses: Array<UserStatus> }) {
  const { data, isPending, isError } = useGetReportsByZip({ zip, userStatuses });
  const reportData = transformReportData(data);
  const [reportType, setReportType] = useState<ReportType>();

  function onViewCreditReport() {
    setReportType(UserStatusType.creditReport);
  }

  function onViewBackgroundReport() {
    setReportType(UserStatusType.backgroundReport);
  }

  return (
    <>
      <Heading level={3} textStyle="heading-sm">
        One last thing to review
      </Heading>
      <Paragraph textStyle="body-sm" css={{ marginBottom: 'default' }}>
        Before you share, review your reports. We’re showing you this because some things, like your credit score, may
        have changed.
      </Paragraph>
      <LoadingMask css={{ display: 'block' }} loading={isPending}>
        {!isError ? (
          <>
            <Card elevated={false} tone="soft">
              <CreditReportOverview
                creditReport={reportData?.creditReport}
                userStatuses={userStatuses}
                onView={onViewCreditReport}
              />
            </Card>
            <Card elevated={false} tone="soft" css={{ marginTop: 'default' }}>
              <BackgroundReportOverview
                backgroundReport={reportData?.backgroundReport}
                userStatuses={userStatuses}
                onView={onViewBackgroundReport}
              />
            </Card>
          </>
        ) : (
          <Alert tone="warning">
            <Text>
              Your screening reports couldn’t be pulled. Contact{' '}
              <Anchor href={APPLICATIONS_FAQ_URL} target="_blank" rel="noopener noreferrer">
                customer support
              </Anchor>{' '}
              to report the issue. You can share your application without the reports.
            </Text>
          </Alert>
        )}
      </LoadingMask>
      <ReportModal
        open={!!reportType}
        userStatuses={userStatuses}
        reportType={reportType}
        reportData={reportData}
        onClose={() => setReportType(undefined)}
      />
    </>
  );
}
